import React from 'react';
import { Box, Grid, Text } from '@chakra-ui/react';
import { motion, useReducedMotion } from 'framer-motion';

const MotionBox = motion(Box);
const MotionLi  = motion.li;

const MODULES = [
  { id: 'M·01', name: 'Locomotion Base',  tag: 'Bipedal / Wheeled'      },
  { id: 'M·02', name: 'Torso Structure',  tag: 'Load-bearing frame'      },
  { id: 'M·03', name: 'Manipulator Arms', tag: '6-DOF / Heavy-duty'      },
  { id: 'M·04', name: 'End Effectors',    tag: 'Gripper / Tool / Sensor' },
  { id: 'M·05', name: 'Sensory Head',     tag: 'Vision / LIDAR / Comms'  },
  { id: 'M·06', name: 'Power Module',     tag: 'Hot-swap battery'        },
  { id: 'M·07', name: 'Compute Core',     tag: 'Edge AI processor'       },
];

function ModularDesign() {
  const shouldReduce = useReducedMotion();
  const ease = [0.25, 0.1, 0.25, 1];

  return (
    <Box as="section" id="modular" aria-label="Modular design"
      bg="oid.bgWhite" py={{ base: '55px', md: '89px' }}>
      <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }}
        gap={{ base: 12, lg: 20 }} alignItems="start"
        maxWidth="1600px" mx="auto" px={{ base: 6, lg: 12 }}>

        {/* Left */}
        <MotionBox
          initial={shouldReduce ? false : { opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.7, ease }}>
          <Box mb={6}>
            <Box as="span" display="block" width="24px" height="2px"
              bg="oid.accent" mb={4} aria-hidden="true" />
            <Text fontSize="11px" fontWeight={600} letterSpacing="0.3em"
              textTransform="uppercase" color="oid.accent">02. Modularity</Text>
          </Box>
          <Text as="h2" fontWeight={700} lineHeight={1.15} color="oid.text"
            sx={{ fontSize: 'clamp(28px, 3.5vw, 48px)' }} mb={6}>
            Designed to come apart.{' '}
            <Text as="span" color="oid.muted" fontWeight={400}>
              Built to work together.
            </Text>
          </Text>
          <Text fontSize="1rem" fontWeight={400} color="oid.muted"
            lineHeight={1.618} maxWidth="440px">
            O-ID's architecture centers on independently replaceable modules.
            Each unit, from locomotion to manipulation, swaps without specialized
            tools or facility downtime. Reconfigure in the field as the mission demands.
          </Text>
        </MotionBox>

        {/* Right — module list */}
        <Box>
          <Box as="ul" listStyleType="none" m={0} p={0}
            aria-label="O-ID module list"
            borderTop="1px solid" borderColor="oid.border">
            {MODULES.map((mod, i) => (
              <MotionLi key={mod.id}
                initial={shouldReduce ? false : { opacity: 0, x: 14 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.5, delay: i * 0.06, ease }}
                whileHover={{ x: 5 }}
                style={{ borderBottom: '1px solid rgba(22,33,58,0.12)', cursor: 'default' }}>
                <Box display="grid" gridTemplateColumns="52px 1fr auto"
                  alignItems="center" gap={5} py={4}>
                  <Text fontSize="11px" fontWeight={600} letterSpacing="0.15em"
                    color="oid.accent">{mod.id}</Text>
                  <Text fontSize="0.9375rem" fontWeight={600} color="oid.text"
                    letterSpacing="0.04em">{mod.name}</Text>
                  <Text fontSize="11px" fontWeight={400} color="oid.muted"
                    letterSpacing="0.08em" whiteSpace="nowrap">{mod.tag}</Text>
                </Box>
              </MotionLi>
            ))}
          </Box>
        </Box>
      </Grid>
    </Box>
  );
}
export default ModularDesign;
